import React, {
	useEffect,
	useState,
	useCallback,
} from "react";
import {
	Box,
	Grid,
	Paper,
	Typography,
	List,
	ListItem,
	ListItemText,
	Button,
	Snackbar,
	TextField,
	FormControl,
	TableContainer,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Link
} from "@mui/material";
import Portal from "@mui/base/Portal";
import CircularProgress from "@mui/material/CircularProgress";
import { DataContext } from "../../../App";
import fileDownload from "js-file-download";
import { AlertBox } from "../../Shared/Alert/Alert";
import DropDown from "../../Shared/DropDown";
import {
	Form,
	Formik
} from "formik";
import ReactMarkdown from "react-markdown";
import "./GenerateAReturnLabel.scss";
import useFetchData from "../../Shared/FetchData";
import { LoadingStatusTable } from "../../Shared/LoadingStatus/LoadingStatus";
import { getDisplayDate } from "../../../utils/date";
import {
	capitalizeFirstLetter,
	capitalizeFirstLetterOfEachWord
} from "../../../utils/text";

export default function GenerateAReturnLabel({ orderID, seller, warehouseID, orderNumber, warehouseCurrency, productsList, isVisible, requireCourier }) {
	if (!isVisible) return;

	const context = React.useContext(DataContext);
	const [state, setState] = useState({
		isVisibleGenerateReturnLabelDiv: isVisible,
		isVisibleDownloadReturnLabelButton: false,
		isGenerateReturnLabelButtonDisabled: true,
		downloadLabelIsLoading: false,
		returnLabelIsLoading: false,
		returnLabelStatus: null,
		returnLabelResults: "",
		showConfirmationAlert: false,
		returnLabelParcelID: 0,
		downloadReturnLabelStatus: false,
		showDownloadConfirmationAlert: false,
		createReturnErrorMessage: "",
		downloadReturnLabelInfoMessage: "",
	});

	const [selectedReturnCourierId, setSelectedReturnCourierId] = useState(null);
	const [courierServices, setCourierServices] = useState({ data: [], isLoading: false });

	const [returnReasons, setReturnReasons] = useState({ data: [], isLoading: false });
	const [returnReasonID, setReturnReasonID] = useState("");

	const [itemQuantities, setItemQuantities] = useState(new Map());

	const existingReturnLabels = useFetchData(useCallback(() => context.dataProvider.getReturnLabels(seller, orderNumber), [context.dataProvider, seller, orderNumber]));

	useEffect(() => {
		if (orderID) {
			const getReturnCourierServiceList = async () => {
				setCourierServices({ isLoading: true });
				await context.dataProvider.getAllReturnCourierServicesForOrder(seller, orderID)
					.then((response) => {
						setCourierServices({ isLoading: false, data: response.data.sort((a, b) => a.name > b.name ? -1 : 1) });
					})
					.catch(() => {
						setCourierServices({ isLoading: false, data: [] });
					});
			};
			getReturnCourierServiceList();
		}
	}, [orderID, seller, context.dataProvider]);

	useEffect(() => {
		if (warehouseID) {
			const getReturnReasons = async () => {
				setReturnReasons({ isLoading: true });
				await context.dataProvider.getReturnReasonsForWarehouse(warehouseID)
					.then((response) => {
						setReturnReasons({ isLoading: false, data: response.data.sort((a, b) => a.sendcloud_id > b.sendcloud_id ? -1 : 1) });
					})
					.catch(() => {
						setReturnReasons({ isLoading: false, data: [] });
					});
			};
			getReturnReasons();
		}
	}, [warehouseID, context.dataProvider]);

	const handleCourierServiceChange = (idStr) => {
		setSelectedReturnCourierId(String(idStr));

		setState((previousState) => ({
			...previousState,
			isGenerateReturnLabelButtonDisabled:
				itemQuantities.size === 0 || ((idStr === "") && requireCourier === true),
		}));
	};

	const buildCreateReturnLabelBody = () => ({
		order_number: orderNumber,
		shipping_method_id: selectedReturnCourierId ?? "",
		return_reason_id: returnReasonID,
		items: Object.fromEntries(itemQuantities),
	});

	const handleReturnReasonChange = (e) => {
		setReturnReasonID(e);
	};

	const defaultWeightLimit = 15.0;

	const CheckboxList = () => {
		const onQuantityChange = (e, sku) => {
			const currentItemQuantities = new Map(itemQuantities);

			if (e.target.value <= 0) {
				currentItemQuantities.delete(sku);
			} else {
				currentItemQuantities.set(sku, parseInt(e.target.value, 10));
			}

			setItemQuantities(currentItemQuantities);
			setState((previousState) => ({
				...previousState,
				isGenerateReturnLabelButtonDisabled:
					currentItemQuantities.size === 0 || (selectedReturnCourierId === null && requireCourier === true),
			}));
		};

		return (
			<>
				<List sx={{ width: "100%", bgcolor: "background.paper", padding: 1 }}>
					{productsList.map((item, index) => {
						const labelId = `checkbox-list-label-${index}`;
						const itemDisabled = (state.isVisibleDownloadReturnLabelButton || item.weight > defaultWeightLimit);
						return (
							<ListItem
								key={index}
								sx={{ padding: 0 }}
							>
								<ListItemText id={labelId} primary={item.name} secondary={item.sku} sx={{ maxWidth: "300px" }} />
								{item.weight > defaultWeightLimit &&
									<AlertBox
										severity="warning"
										message="This product is too large to be returned with InPost,
										please email support@bezos.ai for an ad-hoc return label"
									/>
								}
								<TextField
									label="Quantity to return"
									type="number"
									size="small"
									sx={{ mr: 1, ml: 1, minWidth: 180 }}
									disabled={itemDisabled}
									defaultValue={itemQuantities.get(item.sku)}
									onChange={(e) => onQuantityChange(e, item.sku)}
									InputProps={{
										inputProps: {
											min: 0, max: item.quantity
										}
									}}
								/> of {item.quantity}
							</ListItem>
						);
					})}
				</List>
				<Grid container spacing={1}>
					<Grid item xs={12} container spacing={2} mb={2}>
						<Grid item xs="auto" sx={{ mt: 2 }}>
							<Button
								variant="contained"
								disabled={state.isGenerateReturnLabelButtonDisabled || state.isVisibleDownloadReturnLabelButton}
								onClick={onClickGenerateReturnLabel}
							>
								Generate Label {state.returnLabelIsLoading &&
									<CircularProgress
										sx={{ color: "#FFFFFF", marginLeft: "0.5rem" }} size={15} />}
							</Button>
						</Grid>
						{state.isVisibleDownloadReturnLabelButton &&
							<Grid item xs="auto" sx={{ mt: 2 }}>
								<Button
									variant="contained"
									onClick={onClickDownloadReturnLabel}
								>
									Download Label {state.downloadLabelIsLoading &&
										<CircularProgress
											sx={{ color: "#FFFFFF", marginLeft: "0.5rem" }}
											size={15} />}
								</Button>
							</Grid>
						}
						<Grid item xs />
					</Grid>
				</Grid>
				<AlertBox
					severity="info"
					message="If a label has been created, but is currently unavailable for download, please wait a moment and click 'Download Label' again.
					If after 5 minutes this is still unavailable please contact support@bezos.ai."
				/>
			</>
		);
	};

	const GenerateReturnLabelProductItems = () => {
		if (typeof productsList === "object" && productsList !== null) {
			return (
				<>
					<CheckboxList />
				</>
			);
		} else {
			return (
				<Typography>No items found</Typography>
			);
		}
	};

	const onClickGenerateReturnLabel = async () => {
		if (!seller) return;

		setState(previousState => {
			return {
				...previousState,
				returnLabelIsLoading: true,
				createReturnErrorMessage: "",
				downloadReturnLabelInfoMessage: "",
			};
		});

		const parcelID = await context.dataProvider.createSellerReturnLabel(seller, buildCreateReturnLabelBody())
			.then(response => {
				if (!response.data.parcel_id || response.data.parcel_id === "undefined") {
					return setState(previousState => {
						return {
							...previousState,
							returnLabelIsLoading: false,
							showConfirmationAlert: true,
							isVisibleGenerateReturnLabelDiv: false,
							returnLabelStatus: false,
						};
					});
				}
				return response.data.parcel_id;
			})
			.catch(error => {
				if (error.response.status === 400) {
					setState(previousState => {
						return {
							...previousState,
							createReturnErrorMessage: capitalizeFirstLetter(error.response.data),
						};
					});
				}

				return setState(previousState => {
					return {
						...previousState,
						returnLabelIsLoading: false,
						showConfirmationAlert: true,
						isVisibleGenerateReturnLabelDiv: false,
						returnLabelStatus: false,
					};
				});
			});

		if (!parcelID) {
			return;
		}

		setState(previousState => {
			return {
				...previousState,
				returnLabelIsLoading: false,
				returnLabelStatus: true,
				showConfirmationAlert: true,
				returnLabelParcelID: parcelID,
				isVisibleDownloadReturnLabelButton: true,
			};
		});

		setItemQuantities(new Map());
	};

	const onClickDownloadReturnLabel = async () => {
		if (!seller || !state.returnLabelParcelID) return;

		setState(previousState => {
			return {
				...previousState,
				downloadLabelIsLoading: true,
				downloadReturnLabelInfoMessage: "",
			};
		});

		await context.dataProvider.downloadSellerReturnLabel(seller, orderNumber, state.returnLabelParcelID)
			.then(response => {
				if (response.status === 202) {
					return setState(previousState => {
						return {
							...previousState,
							downloadLabelIsLoading: false,
							showConfirmationAlert: true,
							returnLabelStatus: false,
							downloadReturnLabelInfoMessage: "Return Label is not yet ready, please click 'Download Label' to retry.",
						};
					});
				}

				if (response.status !== 200) {
					return setState(previousState => {
						return {
							...previousState,
							downloadLabelIsLoading: false,
							showConfirmationAlert: true,
							isVisibleDownloadReturnLabelButton: true,
							returnLabelStatus: false,
							downloadReturnLabelInfoMessage: "",
						};
					});
				}

				fileDownload(response.data, orderNumber + "_Return_Label.pdf");

				setState(previousState => {
					return {
						...previousState,
						downloadLabelIsLoading: false,
						showConfirmationAlert: true,
						returnLabelStatus: true,
						isVisibleGenerateReturnLabelDiv: false,
						downloadReturnLabelInfoMessage: "",
					};
				});
			})
			.catch(() => {
				setState(previousState => {
					return {
						...previousState,
						downloadLabelIsLoading: false,
						showConfirmationAlert: true,
						returnLabelStatus: false,
						downloadReturnLabelInfoMessage: "",
					};
				});
			});

	};

	const ExternalLinks = ({ href, children }) => {
		return (
			<a href={href} target="_blank" rel="noopener noreferrer">
				{children}
			</a>
		);
	};

	const components = {
		a: ExternalLinks,
	};

	const CourierSpecificInformation = () => {

		const selectedReturnCourier = courierServices.data?.find((c) => String(c.id) === selectedReturnCourierId);
		const description = selectedReturnCourier?.description || "";
		const parcelCharacteristics = selectedReturnCourier?.parcel_characteristics || "";
		const warning = selectedReturnCourier?.warning || "";

		return (
			<Box className="courier-description">
				{description.length > 0 &&
					<Grid item xs={12} sx={{ mb: 1, pl: 1, pr: 1 }}>
						<Typography component="div" variant="body1">
							<ReactMarkdown components={components}>{description}</ReactMarkdown>
						</Typography>
					</Grid>
				}
				{parcelCharacteristics.length > 0 &&
					<Grid item xs={12} sx={{ mb: 1, pl: 1, pr: 1 }}>
						<Typography component="div" variant="body1" sx={{ fontWeight: "900" }}>
							Characteristics of the parcel:
						</Typography>
						<List>
							{parcelCharacteristics.map((item, index) => (
								<ListItem key={index} sx={{ p: 0 }}>
									<ListItemText primary={item} />
								</ListItem>
							))}
						</List>
					</Grid>
				}
				{warning.length > 0 &&
					<Grid item xs={12} sx={{ mb: 1, pl: 1, pr: 1 }} className="warning-message">
						<AlertBox
							severity="warning"
							message={<ReactMarkdown>{warning}</ReactMarkdown>}
						/>
					</Grid>
				}
			</Box>
		);
	};

	const GenerateAReturnLabelConfirmation = () => {
		const handleClose = (event, reason) => {
			if (reason === "clickaway") {
				return;
			}
			setState(previousState => {
				return {
					...previousState,
					showConfirmationAlert: false,
					downloadReturnLabelInfoMessage: "",
				};
			});
		};

		return (
			(state.returnLabelStatus !== null) && <Portal>
				<Snackbar open={state.showConfirmationAlert} autoHideDuration={12000} onClose={handleClose}>
					<Box>
						{(state.downloadReturnLabelInfoMessage !== "" ?
							<AlertBox
								severity="info"
								message={state.downloadReturnLabelInfoMessage}
							/> :
							(!state.isVisibleDownloadReturnLabelButton ?
								(state.returnLabelStatus === true ?
									<AlertBox
										severity="success"
										message="Return Label created successfully"
									/>
									:
									(state.createReturnErrorMessage !== "" ?
										<AlertBox
											severity="error"
											message={"Error creating Return Label: " + state.createReturnErrorMessage}
										/>
										:
										<AlertBox
											severity="error"
											message="Error creating Return Label, please try again. If this problem
											continues then contact support@bezos.ai"
										/>))
								:
								(state.returnLabelStatus === true ?
									<AlertBox
										severity="success"
										message="Return Label downloaded successfully"
									/>
									:
									<AlertBox
										severity="error"
										message="Error downloading Return Label, please wait a moment and try again.
										If this problem continues then contact support@bezos.ai"
									/>))
						)}
					</Box>
				</Snackbar>
			</Portal>
		);
	};

	const onClickDownloadExistingLabel = async (label) => {
		setState(previousState => {
			return {
				...previousState,
				downloadReturnLabelInfoMessage: "",
			};
		});

		await context.dataProvider.downloadSellerReturnLabel(seller, label.order_number, label.parcel_id)
			.then(response => {
				if (response.status === 202) {
					return setState(previousState => {
						return {
							...previousState,
							showDownloadConfirmationAlert: true,
							downloadReturnLabelStatus: false,
							downloadReturnLabelInfoMessage: "Return Label is not yet ready, please click 'Download' to retry.",
						};
					});
				}

				if (response.status !== 200 || response.data.size < 1000) {
					return setState(previousState => {
						return {
							...previousState,
							showDownloadConfirmationAlert: true,
							downloadReturnLabelStatus: false,
							downloadReturnLabelInfoMessage: "",
						};
					});
				}

				fileDownload(response.data, label.order_number + "_Return_Label.pdf");

				setState(previousState => {
					return {
						...previousState,
						showDownloadConfirmationAlert: true,
						downloadReturnLabelStatus: true,
						downloadReturnLabelInfoMessage: "",
					};
				});
			})
			.catch(() => {
				setState(previousState => {
					return {
						...previousState,
						showDownloadConfirmationAlert: true,
						downloadReturnLabelStatus: false,
						downloadReturnLabelInfoMessage: "",
					};
				});
			});
	};

	const ReturnLabelsTableData = () => {
		if (existingReturnLabels.isLoading) {
			return (
				<LoadingStatusTable message="Loading ..." colSpan={10} />
			);
		}

		if (!existingReturnLabels.status) return;

		if (typeof existingReturnLabels.results === "object" && existingReturnLabels.results.length > 0) {
			return (
				(existingReturnLabels.results.sort((b, a) => a.created_at < b.created_at ? -1 : 1).map((label, index) =>
					<TableRow key={index}>
						<TableCell className="tc-wrap">{getDisplayDate(label.created_at)}</TableCell>
						<TableCell className="tc-wrap">{(label.number_of_labels * label.rate_per_label).toFixed(2) + " " + warehouseCurrency}</TableCell>
						<TableCell className="tc-wrap">{capitalizeFirstLetterOfEachWord(label.status)}</TableCell>
						<TableCell className="tc-wrap">
							<Link
								href={label.tracking_url}
								target="_blank"
								style={{ textDecoration: "none" }}
							>
								{label.tracking_number}
							</Link>
						</TableCell>
						<TableCell className="tc-wrap">
							<Button
								variant="contained"
								sx={{ mr: 1, mb: 1 }}
								disabled={!label.parcel_id}
								color="info"
								onClick={() => onClickDownloadExistingLabel(label)}
							>
								Download
							</Button>
						</TableCell>
					</TableRow>
				))
			);
		}
	};

	const ReturnLabelsTable = () => {
		return (
			<>
				<Typography component="h2" variant="h6" mb={2} mt={2}>
					Previously Created
				</Typography>
				<TableContainer component={Paper} style={{ maxHeight: 300 }}>
					<Table sx={{ minWidth: 650 }} aria-label="simple table" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell align="left">Created Date</TableCell>
								<TableCell align="left">Cost</TableCell>
								<TableCell align="left">Status</TableCell>
								<TableCell align="left">Tracking Number</TableCell>
								<TableCell align="left"></TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{
								existingReturnLabels.isLoading ?
									<LoadingStatusTable colSpan={7} message={"Loading..."} />
									:
									<ReturnLabelsTableData />
							}
						</TableBody>
					</Table>
				</TableContainer>
				<DownloadReturnLabelConfirmation />
			</>
		);
	};

	const DownloadReturnLabelConfirmation = () => {
		const handleClose = (event, reason) => {
			if (reason === "clickaway") {
				return;
			}
			setState(previousState => {
				return {
					...previousState,
					showDownloadConfirmationAlert: false,
					downloadReturnLabelInfoMessage: "",
				};
			});
		};

		return (
			(state.downloadReturnLabelStatus !== null) && <Portal>
				<Snackbar open={state.showDownloadConfirmationAlert} autoHideDuration={12000} onClose={handleClose}>
					<Box>
						{(state.downloadReturnLabelStatus === true ?
							<AlertBox
								severity="success"
								message="Return Label downloaded successfully"
							/>
							:
							(state.downloadReturnLabelInfoMessage !== "" ?
								<AlertBox
									severity="info"
									message={state.downloadReturnLabelInfoMessage}
								/>
								:
								<AlertBox
									severity="error"
									message="The label download failed, please wait a moment and try again. If this problem continues then contact support@bezos.ai"
								/>))}
					</Box>
				</Snackbar>
			</Portal>
		);
	};

	const GenerateAReturnLabelHiddenDiv = () => {
		return (
			<Box mt={0} mb={4}>
				<Formik
					enableReinitialize
					validateOnChange={true}
					validateOnBlur={true}
					initialValues={{}}
					onSubmit={() => {
					}}>
					<Form noValidate autoComplete="off">
						<Paper sx={{ p: 2, width: "100%" }} elevation={2}>
							<Box mb={0}>
								<Grid container spacing={1}>
									<Grid item xs={12}>
										<Grid item xs="auto">
											<Typography component="h2" variant="h6">
												Generate a Return Label
											</Typography>
										</Grid>
										<Grid item xs />
									</Grid>
									<Grid item xs={12}>
										{
											courierServices.isLoading ?
												(
													<Typography sx={{ m: 1 }}>
														<CircularProgress size={15} sx={{ mr: 0.5 }} />Loading...
													</Typography>
												)
												:
												courierServices.data.length > 0 && courierServices.data !== null ?
													(
														<FormControl sx={{ minWidth: 250 }}>
															<DropDown
																id="courier_service"
																name="courier_service"
																label="Courier Service"
																onChange={handleCourierServiceChange}
																value={selectedReturnCourierId ? selectedReturnCourierId : ""}
																data={courierServices.data.map((c) => ({
																	key: c.id,
																	value: String(c.id),
																	name: c.name + " - " + c.price.toFixed(2) + " " + warehouseCurrency,
																}))}
																required={true}
															/>
														</FormControl>
													)
													:
													<AlertBox
														severity="error"
														message="No available return courier services found. Please contact support@bezos.ai for a return label."
													/>
										}
									</Grid>
									<Grid item xs={12}>
										{
											returnReasons.isLoading ?
												(
													<Typography sx={{ m: 1 }}>
														<CircularProgress size={15} sx={{ mr: 0.5 }} />Loading...
													</Typography>
												)
												:
												returnReasons.data.length > 0 && returnReasons.data !== null ?
													(
														<FormControl sx={{ minWidth: 250 }}>
															<DropDown
																id="return_reason"
																name="return_reason"
																label="Return Reason"
																onChange={handleReturnReasonChange}
																value={returnReasonID}
																data={returnReasons.data.map((r) => ({
																	key: r.id,
																	value: r.sendcloud_id,
																	name: r.name,
																}))}
																required={true}
															/>
														</FormControl>
													)
													:
													<AlertBox
														severity="error"
														message="Could not fetch available Return Reasons. Please contact support@bezos.ai for a return label."
													/>
										}
									</Grid>
									<CourierSpecificInformation />
									<Grid item xs={12} mt={1}>
										<Typography component="h6" sx={{ fontWeight: 900 }}>
											Returned Items:
										</Typography>
									</Grid>
									<Grid item xs={12}>
										<GenerateReturnLabelProductItems />
									</Grid>
									{
										existingReturnLabels.results.length > 0 &&
										<Grid item xs={12} mt={1}>
											<ReturnLabelsTable />
										</Grid>
									}
								</Grid>
							</Box>
						</Paper>
					</Form>
				</Formik>
			</Box>
		);
	};

	return (
		<>
			{state.isVisibleGenerateReturnLabelDiv && <GenerateAReturnLabelHiddenDiv />}
			<GenerateAReturnLabelConfirmation />
		</>
	);

}
